import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Input,
  InputGroup,
  Label,
  Card,
  CardBody,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import StudentCourse from "../../components/Student/StudentCourse";
import { BounceLoader } from "react-spinners";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import jump from "jump.js";
import {
  FaCaretDown,
  FaCheck,
  FaRegStar,
  FaSortAmountDown,
  FaStar,
} from "react-icons/fa";
import { GetStudentCourses } from "../../actions/studentQueries";
import { SetFavoriteCourse } from "../../actions/studentMutations";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";
import { jwtDecode } from "jwt-decode";

const showOnlyFavoriteCoursesKey = () =>
  `ShowOnlyFavoriteCourses_${jwtDecode(localStorage.getItem("token")).sub}`;

function Student() {
  const intl = useIntl();
  const [filterSearch, setFilterSearch] = useState("");
  const [showButton, setShowButton] = useState(false);
  const [sortedCourses, setSortedCourses] = useState([]);
  const [sortBy, setSortBy] = useState("courseOrder");
  const [sortingDropdownOpen, setSortingDropdownOpen] = useState(false);
  const [showOnlyFavoriteCourses, setShowOnlyFavoriteCourses] = useState(
    () => localStorage.getItem(showOnlyFavoriteCoursesKey()) === "true",
  );
  const studentCourses = GetStudentCourses();
  const queryClient = useQueryClient();

  const setFavoriteCourse = useMutation({
    mutationFn: SetFavoriteCourse,
    mutationKey: ["SetFavoriteCourse"],
    scope: { id: "SetFavoriteCourse" },
    onMutate: async ({ courseId, isFavorite }) => {
      await queryClient.cancelQueries({ queryKey: ["FetchStudentCourses"] });
      queryClient.setQueryData(["FetchStudentCourses"], (courses) =>
        courses.map((course) =>
          course.id === courseId ? { ...course, isFavorite } : course,
        ),
      );
    },
    onError: (err) => toast.warn(formatServiceError(intl, err?.response?.data)),
    onSettled: () => {
      if (
        queryClient.isMutating({ mutationKey: ["SetFavoriteCourse"] }) === 1
      ) {
        queryClient.invalidateQueries({ queryKey: ["FetchStudentCourses"] });
      }
    },
  });

  const scrollToTop = () => {
    jump("body", { duration: 600 });
  };

  useEffect(() => {
    const handleScrollButton = () => {
      setShowButton(window.scrollY > 200);
    };

    window.addEventListener("scroll", handleScrollButton);
    return () => window.removeEventListener("scroll", handleScrollButton);
  }, []);

  useEffect(() => {
    const sortByStatus = (items, statusOrder) => {
      return [...items].sort(
        (a, b) => statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status),
      );
    };

    const latestRunTime = (course) => {
      const runTimes = course.modules
        .flatMap((mod) => [
          mod.lastRun,
          mod.fmLastRun,
          ...mod.finalExamVariants.map((variant) => variant.runAt),
        ])
        .filter(Boolean)
        .map((timestamp) => new Date(timestamp).getTime());

      return runTimes.length ? Math.max(...runTimes) : null;
    };
    if (!studentCourses.isPending && studentCourses.data?.length > 0)
      switch (sortBy) {
        case "courseOrder": {
          setSortedCourses([...studentCourses.data]);
          break;
        }
        case "InProgress": {
          setSortedCourses(
            sortByStatus(studentCourses.data, [
              "InProgress",
              "NotStarted",
              "Done",
            ]),
          );
          break;
        }
        case "done": {
          setSortedCourses(
            sortByStatus(studentCourses.data, [
              "Done",
              "InProgress",
              "NotStarted",
            ]),
          );
          break;
        }
        case "lastRun": {
          setSortedCourses(
            [...studentCourses.data].sort((a, b) => {
              const latestA = latestRunTime(a);
              const latestB = latestRunTime(b);

              if (latestA === null) return latestB === null ? 0 : 1;
              if (latestB === null) return -1;

              return latestB - latestA;
            }),
          );
          break;
        }
        default: {
          setSortedCourses([...studentCourses.data]);
        }
      }
  }, [studentCourses.data, studentCourses.isPending, sortBy]);

  const showLoader = () => {
    return (
      <div
        className="sweet-loading"
        style={{ position: "fixed", top: "50%", left: "50%" }}
      >
        <BounceLoader color={"#fff"} loading={true} />
      </div>
    );
  };
  const toggle = () => setSortingDropdownOpen((prevState) => !prevState);

  if (studentCourses.isPending) {
    return showLoader();
  }
  let mappedInputSearch = null;
  let mappedCourses = null;

  if (studentCourses.error) {
    mappedCourses = (
      <Col
        xs="12"
        sm="12"
        md="12"
        xl="12"
        lg="12"
        className="d-flex flex-column align-items-center justify-content-center text-center"
      >
        <BounceLoader color={"#869fac"} loading={true} />
        <div className="text-primary lead">
          <FormattedMessage id="Views.Student.NoCourses" />
        </div>
      </Col>
    );
  } else {
    mappedInputSearch = (
      <Row className="fixed-header">
        <Col xs="auto" sm="6" md="6" className="ps-lg-5 ps-3">
          <span className="d-flex flex-wrap align-items-center gap-2 gap-sm-3">
            <Dropdown isOpen={sortingDropdownOpen} toggle={toggle}>
              <DropdownToggle
                outline
                color="primary"
                style={{ fontSize: "initial" }}
              >
                <Row className="align-items-center d-flex mx-0 gap-1">
                  <Col xs="auto" className="d-flex align-items-center px-0">
                    <FaSortAmountDown />
                  </Col>
                  <Col xs="auto" className="d-flex align-items-center px-0">
                    <FormattedMessage id="Sort" />
                  </Col>
                  <Col xs="auto" className="d-flex align-items-center px-0">
                    <FaCaretDown />
                  </Col>
                </Row>
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem onClick={() => setSortBy("courseOrder")}>
                  {sortBy === "courseOrder" && <FaCheck className="me-1" />}
                  <FormattedMessage id="CourseOrder" />
                </DropdownItem>
                <DropdownItem onClick={() => setSortBy("InProgress")}>
                  {sortBy === "InProgress" && <FaCheck className="me-1" />}
                  <FormattedMessage id="OngoingFirst" />
                </DropdownItem>
                <DropdownItem onClick={() => setSortBy("done")}>
                  {sortBy === "done" && <FaCheck className="me-1" />}
                  <FormattedMessage id="DoneFirst" />
                </DropdownItem>
                <DropdownItem onClick={() => setSortBy("lastRun")}>
                  {sortBy === "lastRun" && <FaCheck className="me-1" />}
                  <FormattedMessage id="LastRun" />
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
            <Button
              outline
              color="primary"
              className="student-favorites-filter"
              active={showOnlyFavoriteCourses}
              aria-pressed={showOnlyFavoriteCourses}
              title={intl.formatMessage({ id: "ShowOnlyFavorites" })}
              style={{ fontSize: "initial" }}
              onClick={() => {
                localStorage.setItem(
                  showOnlyFavoriteCoursesKey(),
                  !showOnlyFavoriteCourses,
                );
                setShowOnlyFavoriteCourses(!showOnlyFavoriteCourses);
              }}
            >
              <Row className="align-items-center d-flex mx-0 gap-1">
                <Col xs="auto" className="d-flex align-items-center px-0">
                  {showOnlyFavoriteCourses ? <FaStar /> : <FaRegStar />}
                </Col>
                <Col
                  xs="auto"
                  className="d-none d-sm-flex align-items-center px-0"
                >
                  <FormattedMessage id="Favorites" />
                </Col>
              </Row>
            </Button>
          </span>
        </Col>
        <Col xs sm="6" md="6" className="pe-lg-5 pe-3">
          <InputGroup
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              width: "60%",
              marginLeft: "auto",
            }}
          >
            <Label
              for="searchInput"
              className="d-none d-sm-block"
              style={{ marginBottom: "0", paddingRight: "5px" }}
            >
              <FontAwesomeIcon icon="search" size="2x" />
            </Label>

            <Input
              className="form-control"
              id="searchInput"
              placeholder={intl.formatMessage({ id: "View.Student.SearchCourse" })}
              onChange={(data) => setFilterSearch(data.target.value)}
              style={{
                borderRadius: "25px",
                padding: "10px 20px",
                fontSize: "1rem",
              }}
            />
          </InputGroup>
        </Col>
      </Row>
    );
  }

  if (sortedCourses.length) {
    var currentResult = sortedCourses;
    if (filterSearch.length > 0) {
      currentResult = currentResult.filter(
        (course) =>
          course.name.toLowerCase().indexOf(filterSearch.toLowerCase()) !== -1
      );
    }
    if (showOnlyFavoriteCourses) {
      currentResult = currentResult.filter((course) => course.isFavorite);
    }

    mappedCourses = currentResult.map((course) => (
      <Col
        md="4"
        xl="4"
        sm="6"
        xs="12"
        key={course.id}
        style={{ marginBottom: "1.875rem" }}
      >
        <StudentCourse
          course={course}
          onToggleFavorite={() =>
            setFavoriteCourse.mutate({
              courseId: course.id,
              isFavorite: !course.isFavorite,
            })
          }
        />
      </Col>
    ));

    if (
      showOnlyFavoriteCourses &&
      !sortedCourses.some((course) => course.isFavorite)
    ) {
      mappedCourses = (
        <Col
          xs="12"
          sm="12"
          md="12"
          xl="12"
          lg="12"
          className="d-flex flex-column align-items-center justify-content-center text-center"
        >
          <div className="text-primary lead">
            <FormattedMessage id="NoFavoriteCoursesHint" />
          </div>
        </Col>
      );
    }
  }

  return (
    <div className="animated studentLandingOverview">
      <div className="studentSearch">{mappedInputSearch}</div>
      <Card style={{ border: "1px solid #fff", padding: "1rem" }}>
        <CardBody className="mt-5">
          <Row xs="1" sm="2" md="3" xl="3" lg="3" className="d-flex">
            {mappedCourses}
          </Row>
        </CardBody>
      </Card>

      {showButton && (
        <Button onClick={scrollToTop} className="backToTopButton">
          <FontAwesomeIcon icon="arrow-up" size="lg" color="#fff" />
        </Button>
      )}
    </div>
  );
}

export default Student;
